/**
 * The `parseQueryString` function parses a URL and extracts its query parameters into an object.
 * @param {string} url - The URL containing the query string to parse.
 * @returns {Object|null} - An object representing the parsed query parameters or null if no query string is found.
 */
const parseQueryString = (url) => {
  url = decodeURIComponent(url) // Decode the URL to handle any encoded characters.
  const queryString = url?.split('?')[1] // Extract the query string part of the URL.
  const pairs = queryString?.split('&') // Split the query string into individual key-value pairs.

  if (!pairs) {
    // If there are no pairs, return null.
    return null
  }

  const result = {} // Initialize the result object.

  pairs.forEach((pair) => {
    let [key, value] = pair?.split('=') // Split each pair into key and value.

    // Convert numeric values to numbers.
    if (!isNaN(value)) {
      value = Number(value)
    }

    let keys = key.split(/[\[\]]/).filter((k) => k) // Split the key by '[' and ']' to handle nested objects/arrays.
    let current = result // Start with the root of the result object.

    keys.forEach((k, i) => {
      if (k === 'search') {
        // Skip reserved key 'search'.
        return false
      }

      if (i === keys.length - 1) {
        // If it's the last key segment.
        if (k in current && Array.isArray(current[k])) {
          current[k].push(value) // If key already exists as an array, push the new value.
        } else if (k in current) {
          current[k] = [current[k], value] // If key already exists, convert it to an array with the new value.
        } else {
          current[k] = value // Otherwise, simply assign the value.
        }
      } else {
        // If it's not the last key segment.
        if (!(k in current)) {
          const nextKeyIsArray = keys[i + 1] === '' || !isNaN(keys[i + 1]) // Determine if the next key indicates an array.
          current[k] = nextKeyIsArray ? [] : {} // Initialize the current key as an array or object.
        }
        current = current[k] // Move to the next level in the result object.
      }
    })
  })

  /**
   * Helper function to merge arrays into an object.
   * @param {Array} arrs - The arrays to merge.
   * @returns {Array} - An array of merged objects.
   */
  const mergeArraysToObject = (arrs) => arrs[0].map((_, i) => arrs.reduce((acc, arr) => ({ ...acc, ...arr[i] }), {}))

  /**
   * Adjusts the fields in the result object by merging arrays into objects where necessary.
   * @param {Object} obj - The object to adjust.
   */
  const adjustFields = (obj) => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key]
      if (typeof value === 'object' && !(value instanceof Array)) {
        // If the value is an object and not an array.
        adjustFields(value) // Recursively adjust the fields.

        // Special handling for certain keys.
        if (['creatorNiche', 'audienceAge', 'tags', 'preferences'].includes(key)) {
          const keys = Object.keys(value)
          if (keys.some((k) => Array.isArray(value[k]))) {
            // Check if any sub-value is an array.
            obj[key] = mergeArraysToObject(keys.map((k) => value[k].map((v) => ({ [k]: v }))))
          }
        }

        // creatorSize is serialized as numeric-indexed objects — convert to array
        if (key === 'creatorSize') {
          const keys = Object.keys(value)
          if (keys.every((k) => !isNaN(k))) {
            obj[key] = keys.sort((a, b) => a - b).map((k) => value[k])
          }
        }
      }
    })
  }

  adjustFields(result) // Adjust fields in the result object.

  return result // Return the final parsed and adjusted result object.
}

export default parseQueryString
